import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { titleize } from '@/utils/strings';
import { isValidNumber } from '@/utils/numbers';
import { checkHasUpdates } from './utils';
import { Label, Input, Select } from '@/components/FormElements';
import ColorPicker from '@/components/Editor/Form/fields/common/ColorPicker';
import Button from '@/components/Button';
import parentStyles from '@/features/WidgetEdit/Form/styles.module.scss';
import styles from './styles.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export let BorderStyle;

(function (BorderStyle) {
  BorderStyle["None"] = "none";
  BorderStyle["Hidden"] = "hidden";
  BorderStyle["Dotted"] = "dotted";
  BorderStyle["Dashed"] = "dashed";
  BorderStyle["Solid"] = "solid";
  BorderStyle["Double"] = "double";
  BorderStyle["Groove"] = "groove";
  BorderStyle["Ridge"] = "ridge";
  BorderStyle["Inset"] = "inset";
  BorderStyle["Outset"] = "outset";
  BorderStyle["Initial"] = "initial";
  BorderStyle["Inherit"] = "inherit";
})(BorderStyle || (BorderStyle = {}));

const EditorFieldsBorder = _ref => {
  let {
    width,
    style,
    color,
    backgroundColor,
    nameSpace,
    defaults = {},
    textOverrides = {},
    enabledButtonStyles = Object.values(BorderStyle),
    enableGradientPicker = false,
    pill = true,
    minWidth = 0,
    colorPallet,
    children,
    onChangeWidth,
    onChangeStyle,
    onChangeColor,
    onDelete
  } = _ref;
  const widthFieldId = `${nameSpace}-border-width`;
  const styleFieldId = `${nameSpace}-border-style`;
  const colorFieldId = `${nameSpace}-border-color`;
  const defaultWidth = typeof defaults.width === 'number' ? defaults.width : undefined;
  const defaultStyle = defaults.style ? defaults.style : BorderStyle.Solid;
  const defaultColor = defaults.color !== undefined ? defaults.color : '#000'; // Re-render the color picker when inputs reset

  const {
    0: updatedAt,
    1: setUpdatedAt
  } = useState(Date.now());
  const hasUpdates = useMemo(() => checkHasUpdates({
    width,
    style,
    color,
    backgroundColor
  }), [width, style, color, backgroundColor]); // Reset all inputs back to default
  // and invoke on delete upon completion

  const handleDelete = useCallback(() => {
    try {
      [{
        id: widthFieldId,
        defaultValue: defaultWidth
      }, {
        id: styleFieldId,
        defaultValue: defaultStyle
      }, {
        id: colorFieldId,
        defaultValue: defaultColor
      }].forEach(_ref2 => {
        let {
          id,
          defaultValue
        } = _ref2;
        const field = document.getElementById(id);
        if (!field) return; // @ts-ignore

        field.value = typeof defaultValue === undefined ? '' : defaultValue;
      });
      setUpdatedAt(Date.now()); // eslint-disable-next-line no-empty
    } catch (err) {}

    if (onDelete) {
      onDelete();
    }
  }, [widthFieldId, defaultWidth, styleFieldId, defaultStyle, colorFieldId, defaultColor, onDelete]);
  return /*#__PURE__*/_jsxs("fieldset", {
    className: clsx(parentStyles.formField, {
      [parentStyles['formField--pill']]: pill
    }),
    children: [pill ? /*#__PURE__*/_jsx("legend", {
      className: parentStyles.legend,
      children: (textOverrides === null || textOverrides === void 0 ? void 0 : textOverrides.legend) || 'Border'
    }) : null, /*#__PURE__*/_jsxs("div", {
      className: parentStyles.formField__grid,
      children: [onChangeWidth ? /*#__PURE__*/_jsx(Label, {
        forInput: widthFieldId,
        text: "Width",
        variant: pill ? 'small' : undefined,
        children: /*#__PURE__*/_jsx(Input, {
          id: widthFieldId,
          type: "number",
          step: "1",
          min: minWidth,
          placeholder: textOverrides.widthPlaceholder ?? '1',
          value: isValidNumber(width) ? Math.max(width, minWidth) : undefined,
          defaultValue: isValidNumber(width) ? width : defaultWidth,
          onChange: evt => {
            const value = parseInt(`${evt.target.value || ''}`.trim(), 10);
            onChangeWidth(isNaN(value) ? null : Math.max(minWidth, value));
          }
        })
      }) : /*#__PURE__*/_jsx(_Fragment, {}), onChangeStyle ? /*#__PURE__*/_jsx(Label, {
        forInput: styleFieldId,
        text: "Style",
        variant: pill ? 'small' : undefined,
        children: /*#__PURE__*/_jsx(Select, {
          id: styleFieldId,
          defaultValue: style || defaultStyle,
          onChange: evt => {
            // @ts-ignore
            onChangeStyle(evt.target.value);
          },
          children: enabledButtonStyles.map(borderStyle => /*#__PURE__*/_jsx("option", {
            value: borderStyle,
            children: titleize(borderStyle)
          }, `${nameSpace}-${borderStyle}`))
        })
      }) : /*#__PURE__*/_jsx(_Fragment, {})]
    }), onChangeColor ? /*#__PURE__*/_jsx(ColorPicker, {
      id: colorFieldId,
      label: (textOverrides === null || textOverrides === void 0 ? void 0 : textOverrides.colorPickerLabel) || 'Color',
      size: pill ? 'small' : undefined,
      caption: (textOverrides === null || textOverrides === void 0 ? void 0 : textOverrides.colorPickerCaption) || 'Optional border color',
      className: styles.colorPicker,
      defaultValue: color || defaultColor,
      defaults: {
        solid: color || defaultColor
      },
      enableGradientPicker: enableGradientPicker,
      colorPallet: colorPallet,
      onChange: color => onChangeColor(typeof color === 'string' ? color : null)
    }, `${colorFieldId}-${updatedAt}`) : /*#__PURE__*/_jsx(_Fragment, {}), children, onDelete && hasUpdates ? /*#__PURE__*/_jsx(Button, {
      type: "button",
      variant: "delete",
      size: pill ? 'small' : undefined,
      className: styles.deleteButton,
      onClick: handleDelete,
      children: (textOverrides === null || textOverrides === void 0 ? void 0 : textOverrides.clearBorder) || 'Clear Border'
    }) : /*#__PURE__*/_jsx(_Fragment, {})]
  });
};

export default EditorFieldsBorder;